import { Timestamp } from 'firebase/firestore';

// ============================================
// User & Settings
// ============================================

export interface UserSettings {
  shutdownTime: string; // HH:MM format (e.g., "17:00")
  defaultBlockDuration: number; // minutes
  theme: 'light' | 'dark';
  defaultStartHour: number; // 0-23
  notifications: boolean;
  showRevisionMarks: boolean; // Visual chaos toggle
  sketchyLineStyle: boolean;

  // Cal Newport Features
  fixedScheduleEnabled: boolean; // Fixed-schedule productivity
  fixedScheduleEndTime: string; // HH:MM format (e.g., "17:00")
  fixedScheduleReminderMinutes: number; // minutes before to remind
  showDeepWorkMetrics: boolean; // Show deep work hours in UI
  weeklyPlanningEnabled: boolean; // Enable weekly planning features
  dayThemingEnabled: boolean; // Enable day theming
}

export interface User {
  uid: string;
  email: string;
  displayName: string;
  settings: UserSettings;
  timezone: string;
  subscriptionTier: 'free' | 'paid';
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ============================================
// Blocks
// ============================================

export type BlockStatus = 'planned' | 'completed' | 'missed' | 'cancelled';

// Reasons for missing a block (optional tracking)
export type MissedReason = 'slept' | 'interrupted' | 'low_energy' | 'chose_not_to' | 'other';

export const MISSED_REASON_LABELS: Record<MissedReason, string> = {
  slept: 'Went to sleep',
  interrupted: 'Got interrupted',
  low_energy: 'Low energy',
  chose_not_to: 'Chose not to',
  other: 'Other reason',
};

export interface Block {
  id: string;
  userId: string;
  date: string; // YYYY-MM-DD
  startTime: number; // minutes from midnight (e.g., 540 = 9:00 AM)
  duration: number; // minutes
  title: string;
  notes: string;
  categoryId: string;
  color: string; // hex (e.g., "#2b6cb0")
  status: BlockStatus;
  linkedAlternativeId: string | null;
  isMIT?: boolean; // Most Important Task marker
  mitOrder?: number; // 1, 2, or 3 for MIT priority

  // Duration history (for in-block indicators)
  originalDuration?: number; // First planned duration
  durationChangedAt?: Timestamp; // When duration was changed

  // Missed block tracking
  missedReason?: MissedReason; // Optional reason when status is 'missed'
  missedAt?: Timestamp; // When the block was marked as missed

  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface BlockRevision {
  id: string;
  snapshot: Omit<Block, 'id'>; // full block data at that moment
  changedFields: string[]; // which fields changed
  createdAt: Timestamp;
}

export interface BlockWithRevisions extends Block {
  revisions: BlockRevision[];
}

// ============================================
// Tasks
// ============================================

export type TaskPriority = 'low' | 'medium' | 'high';
export type TaskStatus = 'pending' | 'scheduled' | 'completed';

export interface Task {
  id: string;
  userId: string;
  title: string;
  notes: string;
  estimatedDuration: number; // minutes
  dueDate: string | null; // YYYY-MM-DD
  priority: TaskPriority;
  categoryId: string;
  status: TaskStatus;
  scheduledBlockId: string | null;
  order: number; // for manual sorting
  isMIT?: boolean; // Most Important Task marker
  mitDate?: string; // YYYY-MM-DD format - date this task is an MIT for
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ============================================
// Categories
// ============================================

export interface Category {
  id: string;
  userId: string | null; // null = system default
  name: string;
  color: string; // hex
  icon: string | null;
  isDefault: boolean;
  order: number;
}

// ============================================
// Days
// ============================================

export interface Day {
  id: string;
  userId: string;
  date: string; // YYYY-MM-DD
  blockIds: string[]; // references to blocks
  ritualCompleted: boolean;
  ritualCompletedAt: Timestamp | null;
  reflection: string | null;
  quote: string | null;
  carryOverBlockIds: string[]; // blocks moved from previous day
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ============================================
// Shutdown Ritual
// ============================================

export type RitualStep =
  | 'review'
  | 'revision-summary'
  | 'unfinished'
  | 'capture'
  | 'plan-tomorrow'
  | 'reflection'
  | 'quote';

export type UnfinishedDecision = 'carry-over' | 'delete' | 'reschedule';

export interface RitualState {
  currentStep: RitualStep;
  completedSteps: RitualStep[];
  reviewData: {
    completedBlocks: string[];
    missedBlocks: string[];
  };
  unfinishedDecisions: Record<string, UnfinishedDecision>;
  capturedTasks: string[];
  tomorrowBlocks: Block[];
  reflection: string;
  quote: Quote;
}

// ============================================
// Quotes
// ============================================

export interface Quote {
  text: string;
  author: string;
}

// ============================================
// Capture / Inbox
// ============================================

export type CaptureType = 'thought' | 'task' | 'tomorrow' | 'soon' | 'note' | 'quick-block';
export type CaptureStatus = 'inbox' | 'processed' | 'archived';

export interface Capture {
  id: string;
  userId: string;
  content: string;
  type: CaptureType;
  createdAt: Date;
  processedAt?: Date;
  status: CaptureStatus;
  convertedTo?: {
    type: 'task' | 'block';
    id: string;
  };
  tags?: string[];
}

// ============================================
// Most Important Tasks (MIT)
// ============================================

export interface DailyMIT {
  id: string;
  taskId?: string;
  blockId?: string;
  title: string;
  completed: boolean;
  order: number; // 1, 2, or 3
}

// ============================================
// Revision Log
// ============================================

export type RevisionType = 'move' | 'delete' | 'resize' | 'edit' | 'add';

export interface RevisionLog {
  id: string;
  date: string; // YYYY-MM-DD
  timestamp: Date;
  type: RevisionType;
  blockId: string;
  blockTitle: string;

  // For moves
  fromStartTime?: number;
  fromDuration?: number;
  toStartTime?: number;
  toDuration?: number;

  // For resizes
  oldDuration?: number;
  newDuration?: number;

  // For deletes
  deletedStartTime?: number;
  deletedDuration?: number;

  // For adds
  addedStartTime?: number;
  addedDuration?: number;

  // Optional context
  reason?: string;
  planVersion?: number;
}

// ============================================
// Weekly Planning (Cal Newport)
// ============================================

export type DayTheme =
  | 'deep-work'
  | 'meetings'
  | 'admin'
  | 'creative'
  | 'learning'
  | 'rest'
  | 'flex'
  | null;

export const DAY_THEME_LABELS: Record<NonNullable<DayTheme>, string> = {
  'deep-work': 'Deep Work',
  meetings: 'Meetings',
  admin: 'Admin',
  creative: 'Creative',
  learning: 'Learning',
  rest: 'Rest',
  flex: 'Flexible',
};

export const DAY_THEME_ICONS: Record<NonNullable<DayTheme>, string> = {
  'deep-work': '🎯',
  meetings: '👥',
  admin: '📋',
  creative: '💡',
  learning: '📚',
  rest: '🌿',
  flex: '🔄',
};

export interface WeeklyGoal {
  id: string;
  title: string;
  completed: boolean;
  order: number;
}

export interface WeeklyPlan {
  id: string; // YYYY-Www format (e.g., "2024-W03")
  userId: string;
  weekStart: string; // YYYY-MM-DD (Monday)
  weekEnd: string; // YYYY-MM-DD (Sunday)
  goals: WeeklyGoal[];
  dayThemes: Record<string, DayTheme>; // YYYY-MM-DD -> theme
  notes: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ============================================
// Deep Work Metrics
// ============================================

export interface DailyMetrics {
  date: string; // YYYY-MM-DD
  deepWorkMinutes: number;
  shallowWorkMinutes: number;
  totalBlockedMinutes: number;
  completedBlockCount: number;
  plannedBlockCount: number;
  missedBlockCount: number; // Blocks marked as missed
  missedMinutes: number; // Total time in missed blocks
  revisionCount: number;
  mitsCompleted: number;
  mitsTotal: number;
}

export interface WeeklyMetrics {
  weekId: string; // YYYY-Www format
  weekStart: string;
  weekEnd: string;
  totalDeepWorkMinutes: number;
  totalShallowWorkMinutes: number;
  averageDeepWorkPerDay: number;
  bestDeepWorkDay: string | null;
  goalsCompleted: number;
  goalsTotal: number;
  dailyBreakdown: DailyMetrics[];
}

// ============================================
// Fixed Schedule (Cal Newport)
// ============================================

export interface FixedSchedule {
  enabled: boolean;
  endTime: string; // HH:MM format
  reminderMinutes: number; // minutes before end time to show reminder
  strictMode: boolean; // if true, show stronger warnings
}

// ============================================
// UI State
// ============================================

export interface TimeSlot {
  hour: number;
  minute: number;
}

export interface DragItem {
  type: 'task' | 'block';
  id: string;
  data: Task | Block;
}
